import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Reason } from "../../types/reason";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetReasonByIdQuery,
  useAddReasonMutation,
  useEditReasonMutation,
} from "../../services/reason.service";
import { BaseModal, StyledTextField, Button, SelectBase, SelectOption } from "../../components/global";

const typeOptions: SelectOption[] = [
  { value: "cancel-worker", label: "cancel-worker" },
  { value: "cancel-job", label: "cancel-job" },
  { value: "reschedule-job", label: "reschedule-job" },
  { value: "edit-job", label: "edit-job" },
  { value: "manual-debit", label: "manual-debit" },
  { value: "manual-credit", label: "manual-credit" },
  { value: "reject-work", label: "reject-work" },
];

interface ModalReasonFormProps {
  open: boolean;
  onClose: () => void;
  data: Reason | null;
}

const ModalReasonForm = ({ open, onClose, data }: ModalReasonFormProps) => {
  const { data: reason } = useGetReasonByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useAddReasonMutation();
  const [edit, { isLoading: editing }] = useEditReasonMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("name", data?.name || "");
      setValue("description", data?.description || "");
      setValue("type", data?.type || typeOptions[0].value);
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (reason) {
      setValue("name", reason?.name || "");
      setValue("description", reason?.description || "");
      setValue("type", reason?.type || typeOptions[0].value);
    }
  }, [reason, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} Reason`}
      maxWidth="xs"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Reason Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.name}
                  helperText={!!errors.name && "This field is required"}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Description"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  multiline
                  rows={3}
                  placeholder="Enter description (optional)"
                />
              )}
            />
            <Controller
              name="type"
              control={control}
              render={({ field: { onChange, value } }) => {
                const selectedOption = typeOptions.find(
                  (opt) => opt.value === value
                ) || typeOptions[0];
                return (
                  <SelectBase
                    options={typeOptions}
                    value={selectedOption}
                    onChange={(newValue) => {
                      if (newValue && !Array.isArray(newValue)) {
                        onChange(newValue.value);
                      } else {
                        onChange(typeOptions[0].value);
                      }
                    }}
                    label="Type"
                    placeholder="Select type..."
                  />
                );
              }}
            />
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalReasonForm;
